import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './Modal';
import { useStore } from '../store';
import type { Node } from '../types';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Node[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const nodes = useStore((state) => state.nodes);
  const selectNode = useStore((state) => state.selectNode);
  const panToElement = useStore((state) => state.panToElement);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const matchingNodes = Object.values(nodes).filter((node) =>
      node.text.toLowerCase().includes(lowerQuery)
    );
    setResults(matchingNodes);
  }, [query, nodes]);

  const handleSelectNode = (nodeId: string) => {
    selectNode(nodeId);
    panToElement(nodeId);
    onClose();
    setQuery('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && results.length > 0) {
      handleSelectNode(results[0].id);
    }
  };

  const handleClose = () => {
    onClose();
    setQuery('');
    setResults([]);
  };

  const getNodeTypeColor = (type: string) => {
    switch (type) {
      case 'task':
        return 'var(--color-node-task)';
      case 'note':
        return 'var(--color-node-note)';
      case 'milestone':
        return 'var(--color-node-milestone)';
      default:
        return 'var(--color-text-secondary)';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Search Nodes">
      <input
        ref={inputRef}
        type="text"
        className="modal-input"
        placeholder="Type to search..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />

      {query && (
        <div className="search-results">
          {results.length > 0 ? (
            results.map((node) => (
              <div
                key={node.id}
                className="search-result-item"
                onClick={() => handleSelectNode(node.id)}
              >
                <div className="search-result-text">
                  {node.text || 'Untitled'}
                </div>
                <div
                  className="search-result-type"
                  style={{ color: getNodeTypeColor(node.type) }}
                >
                  {node.type}
                </div>
              </div>
            ))
          ) : (
            <div className="search-no-results">
              No nodes found matching "{query}"
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
